import { api, BASE_URL } from './api';

export interface ClassSession {
  id: number;
  name: string;
  url?: string;
  class_date: string;
  end_time: string;
  end_date: string;
  timezone: string;
  is_active: boolean;
  plan_ids: number[];
  created_at: string;
  updated_at: string;
}

export interface ClassPayload {
  name: string;
  url: string;
  class_date: string;
  end_time: string;
  is_active: boolean;
  plan_ids: number[];
}

interface ClassesResponse {
  success: boolean;
  data: ClassSession[];
  message: string;
}

interface ClassResponse {
  success: boolean;
  message: string;
  data: ClassSession;
}

interface ClassJoinResponse {
  success: boolean;
  message: string;
  data: {
    join_path: string;
    expires_in: number;
  };
}

interface ClassApiPayload extends ClassPayload {
  end_date: string;
  timezone: string;
}

export const getBrowserTimeZone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const toApiPayload = (payload: ClassPayload): ClassApiPayload => {
  const startDate = new Date(payload.class_date);
  const localDate = payload.class_date.slice(0, 10);
  const endDate = new Date(`${localDate}T${payload.end_time.length === 5 ? `${payload.end_time}:00` : payload.end_time}`);

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    throw new Error('Select a valid class start and end time.');
  }
  if (endDate.getTime() <= startDate.getTime()) {
    throw new Error('End time must be after the live class start time.');
  }

  return {
    ...payload,
    class_date: startDate.toISOString(),
    end_date: endDate.toISOString(),
    timezone: getBrowserTimeZone(),
  };
};

const authOptions = (token: string) => ({
  headers: { Authorization: `Bearer ${token}` },
});

export const getClasses = async (token: string): Promise<ClassSession[]> => {
  const response = await api.get<ClassesResponse>('/classes', authOptions(token));
  return response.data;
};

export const createClass = (token: string, payload: ClassPayload): Promise<ClassResponse> =>
  api.post<ClassResponse>('/classes', toApiPayload(payload), authOptions(token));

export const updateClass = (token: string, id: number, payload: ClassPayload): Promise<ClassResponse> =>
  api.put<ClassResponse>(`/classes/${id}`, toApiPayload(payload), authOptions(token));

export const requestClassJoin = async (token: string, id: number): Promise<string> => {
  const response = await api.post<ClassJoinResponse>(`/classes/${id}/join`, {}, authOptions(token));
  return `${BASE_URL}${response.data.join_path}`;
};

export const deleteClass = (token: string, id: number): Promise<{ success: boolean; message: string }> =>
  api.delete<{ success: boolean; message: string }>(`/classes/${id}`, authOptions(token));
